<script type="text/jsx">
import isEmpty from 'lodash-es/isEmpty';
import every from 'lodash-es/every';
import includes from 'lodash-es/includes';
import {
  clearProductEntryCacheByPlanType,
  getDefaultProductEntryData,
  preCheckForProductEntry,
} from './helper/pre';
import {
  BILLING_CYCLE_CONSTANT,
  defaultPlanCategory,
  flowTypes,
  salesChannelList,
  support_all,
  TRIAL_TYPE
} from './constant';
import { EventTracking } from '../../common/trackings';

export default {
  name: 'BillingProductEntry',
  props: {
    // tracking item data info, need append to the link
    eventTrackItemInfo: {
      type: Object,
      default: () => ({}),
    },
    /** base plan category */
    basePlan: {
      type: String,
      default: defaultPlanCategory,
    },
    /** purchasing plan period */
    period: {
      type: String,
      validator(val) {
        if (!val) return true;
        return includes(Object.values(BILLING_CYCLE_CONSTANT), val);
      },
    },
    /** ze/zs/wb capacity */
    capacity: Number,
    /** purchase free trial button */
    freeTrial: Boolean,
    /** product entry type, eg: /pricing#zp */
    entryType: String,
    /** redirect to checkout page, new window */
    newWindow: Boolean,
    /** custom product entry options */
    entryOptions: {
      type: Object,
    },
    /** net new/free account buy free trial plan */
    buyTrialBtnTitle: String,
    /** existing account add free trial plan */
    addTrialBtnTitle: String,
    /** existing free trial plan to upgrade to paid plan */
    trialToPaidBtnTitle: String,
    /** net new/free account buy paid plan */
    buyBtnTitle: String,
    /** existing account(with current paid plan) to edit */
    editBtnTitle: String,
    /** existing account add paid plan */
    addPaidBtnTitle: String,
    /** existing account downgrade current paid plan (biz/bizplus -> pro) */
    downgradeBtnTitle: String,
    /** existing account upgrade current paid plan (pro -> biz/bizplus) */
    upgradeBtnTitle: String,
    /** When you need to get the latest results through the interface every time, you can pass in false, and cache is used by default. */
    useCache: {
      type: Boolean,
      default: true,
    },
    /** button type */
    type: {
      type: String,
      default: 'plain',
    },
    icon: {
      type: String,
      default: '',
    },
    svgIcon: {
      type: String,
      default: '',
    },
    suffixIcon: {
      type: String,
      default: '',
    },
    round: Boolean,
    size: {
      type: String,
      default: 'small'
    },
    circle: Boolean,
    // control which flows need to be displayed
    visibleFlowTypes: {
      type: Array,
      default: () => [support_all], // All qualifying needs to be displayed
      validator(list) {
        return every(list, (type) => {
          const valid = [...Object.values(flowTypes), support_all].includes(type);

          if (!valid) {
            console.error(`props {${type}} is invalid for visibleForCheckTypes`);
          }

          return valid;
        });
      },
    },
    // when the button has disabled status, don't show this button
    inVisibleForDisabled: Boolean,
    // when the button has disabled status, need custom button title
    customDisabledButtonTitle: String,
    // button prefix
    prefix: String,
    // button suffix
    suffix: String,
    // button classnames
    buttonClass: String,
    // custom product entry component
    advanced: Boolean,
    // Do you need to clear the memory interface cache when the component is destroyed?
    deleteCacheForDestroy: Boolean,
    // event tracking object
    trackingPayload: Object,
    // plan category
    categoryCode: String,
    // Will be filtered based on category code
    entryCategoryCode: String,
    // sales channel
    salesChannel: {
      type: String,
      validator(val) {
        if (isEmpty(val)) return true;

        const valid = salesChannelList.includes(val);

        if (!valid) {
          console.error(`props salesChannel is invalid for ${salesChannelList}`);
        }

        return valid;
      }
    },
    buttonAriaLabel: String,
    embed: Boolean,
  },
  data() {
    return {
      entryData: getDefaultProductEntryData(),
    };
  },
  computed: {
    buttonTitle() {
      // when the button has disabled status, need custom button title
      if (this.customDisabledButtonTitle && this.disabled) {
        return this.customDisabledButtonTitle;
      }

      const flowType = this.entryData?.flowType;

      let title = '';

      switch (flowType) {
        case flowTypes.buyTrial:
        case flowTypes.basic2trial:
          title = this.buyTrialBtnTitle;
          break;
        case flowTypes.addTrial:
          title = this.addTrialBtnTitle;
          break;
        case flowTypes.trial2paid:
          title = this.trialToPaidBtnTitle;
          break;
        case flowTypes.buyNow:
        case flowTypes.basic2pro:
          title = this.buyBtnTitle;
          break;
        case flowTypes.add2cart:
          title = this.addPaidBtnTitle;
          break;
        case flowTypes.edit:
          title = this.editBtnTitle;
          break;
        case flowTypes.downgrade:
          title = this.downgradeBtnTitle;
          break;
        case flowTypes.upgrade:
          title = this.upgradeBtnTitle;
          break;
      }

      return title || this.entryData?.buttonTitle;
    },
    disabled() {
      return this.entryData?.disabled;
    },
    visible() {
      // when the button has disabled status, don't show this button
      if (this.inVisibleForDisabled && this.disabled) {
        return false;
      }

      const visibleFlowTypes = this.visibleFlowTypes;
      const flowType = this.entryData?.flowType;

      if (includes(visibleFlowTypes, support_all)) {
        const isTrialService = this.entryData?.trialType === TRIAL_TYPE.service;
        return isTrialService ? true : isEmpty(this.entryData?.link) === false;
      }

      if (includes(visibleFlowTypes, flowType)) {
        return true;
      }

      return false;
    },
    eventTrackingPayload() {
      if (isEmpty(this.trackingPayload)) {
        return undefined;
      }

      return this.trackingPayload;
    },
    productEntryData() {
      return {
        ...this.entryData,
        visible: this.visible,
        disabled: this.disabled,
        buttonTitle: this.buttonTitle,
        eventTrackingPayload: this.eventTrackingPayload,
      };
    },
  },
  methods: {
    handlerBuyNow() {
      const { link } = this.entryData;
      this.$emit('click', this.entryData);

      // event tracking
      EventTracking(this.eventTrackingPayload);

      if (link) {
        if (this.newWindow) {
          return window.open(link, '_blank');
        }

        window.location.href = link;
      }
    },
    replaceEntryData(data) {
      this.entryData = isEmpty(data) ? getDefaultProductEntryData() : data;
    },
    async updateProductEntryData() {
      const options = {
        entryType: this.entryType,
        freeTrial: this.freeTrial,
        basePlan: this.basePlan,
        period: this.period,
        capacity: this.capacity,
        useCache: this.useCache,
        entryOptions: this.entryOptions,
        categoryCode: this.categoryCode,
        salesChannel: this.salesChannel,
        entryCategoryCode: this.entryCategoryCode,
        eventTrackItemInfo: this.eventTrackItemInfo,
        embed: this.embed,
      };

      const result = await preCheckForProductEntry(options);
      this.replaceEntryData(result);
    },
    renderButton() {
      return <zm-button
        circle={this.circle}
        class={this.buttonClass}
        disabled={this.disabled}
        icon={this.icon}
        round={this.round}
        suffixIcon={this.suffixIcon}
        type={this.type}
        size={this.size}
        onClick={this.handlerBuyNow}
        aria-label={this.buttonAriaLabel || this.buttonTitle}
      >
        {this.buttonTitle}
      </zm-button>;
    },
    renderButtonPrefix() {
      if (this.$slots?.prefix) {
        return this.$slots.prefix;
      }

      if (this.$scopedSlots?.prefix) {
        return this.$scopedSlots.prefix?.(this.productEntryData);
      }

      if (this.prefix) {
        return <p class="billing-product-entry__btn-prefix">{this.prefix}</p>;
      }

      return null;
    },
    renderButtonSuffix() {
      if (this.$slots?.suffix) {
        return this.$slots.suffix;
      }

      if (this.$scopedSlots?.suffix) {
        return this.$scopedSlots.suffix?.(this.productEntryData);
      }

      if (this.suffix) {
        return <p class="billing-product-entry__btn-suffix">{this.suffix}</p>;
      }

      return null;
    },
    renderAdvancedComp() {
      if (this.$slots?.default) {
        return this.$slots.default;
      }

      if (this.$scopedSlots?.default) {
        return this.$scopedSlots.default?.(this.productEntryData);
      }

      return <div class="billing-product-entry__wrap">
        {this.renderButtonPrefix()}
        {this.renderButton()}
        {this.renderButtonSuffix()}
      </div>;
    },
  },
  created() {
    this.updateProductEntryData();
  },
  beforeDestroy() {
    if (this.deleteCacheForDestroy) {
      clearProductEntryCacheByPlanType({
        entryType: this.entryType,
        freeTrial: this.freeTrial,
        categoryCode: this.categoryCode,
        salesChannel: this.salesChannel,
      });
    }
  },
  watch: {
    entryData: {
      handler(val) {
        this.$emit('update', val);
      },
    },
    '$props': {
      deep: true,
      handler() {
        this.updateProductEntryData();
      },
    },
  },
  render() {
    if (!this.visible) return null;

    if (this.advanced) {
      return this.renderAdvancedComp();
    }

    return this.renderButton();
  },
};
</script>

<style>
.billing-product-entry__wrap {

}

.billing-product-entry__btn-prefix, .billing-product-entry__btn-suffix  {
  color: #666487;
  font-size: 12px;
  font-weight: 500;
  line-height: 150%;
  margin: 3px 0px 0px;
}
</style>
